import { compose } from 'recompose'
import { connect, ConnectedProps } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router'
import React, { useEffect, useState, useCallback, useRef, Fragment } from 'react'
import { push } from 'connected-react-router'

import { appSelectors, codetableActions, modalDialogActions } from '../../redux'
import { getOpenApiInstance } from '../../api/api'
import { INACTIVITY_TIMEOUT } from '../../constants/commonConstants'
import { intl, IntlInterface, errorHandler } from '../../hoc'
import { ReduxState } from '../../types'
import { reloadPage } from '../../utils/url'
import { Routes } from '../../constants'
import { SHOW_INACTIVITY } from '../../constants/modals'
import BasicLayout from './Components/BasicLayout/BasicLayout'
import ClientAdditionalInfo from '../ClientAdditionalInfo/ClientAdditionalInfo'
import ClientAddress from '../ClientAddress/ClientAddress'
import ClientChecks from '../ClientChecks/ClientChecks'
import ClientDetails from '../ClientDetails/ClientDetails'
import ClientInfo from '../ClientInfo/ClientInfo'
import ClientScoring from '../ClientScoring/ClientScoring'
import Contract from '../Contract/Contract'
import ContractSign from '../ContractSign/ContractSign'
import CreateCif from '../CreateCif/CreateCif'
import ExitScreen from '../ExitScreen/ExitScreen'
import Final from '../Final/Final'
import FinanceEmployer from '../FinanceEmployer/FinanceEmployer'
import FinanceExpenses from '../FinanceExpenses/FinanceExpenses'
import FinanceIncome from '../FinanceIncome/FinanceIncome'
import FinanceProfession from '../FinanceProfession/FinanceProfession'
import Identity from '../Identity/Identity'
import KoOnline from '../KoOnline/KoOnline'
import Availability from '../Availability/Availability'
import KoScoring from '../KoScoring/KoScoring'
import LeaveConfirmation from '../../components/LeaveConfirmation/LeaveConfirmation'
import LoanConfigurator, { LoanConfiguratorRe } from '../LoanConfigurator/LoanConfigurator'
import messages from '../../i18n'
import ModalDialog from '../ModalDialog/ModalDialog'
import ModalLoader from '../ModalLoader/ModalLoader'
import NotFound from '../NotFound/NotFound'
import NotImplementedPage from '../NotImplementedPage/NotImplementedPage'
import OkBranch from '../OkBranch/OkBranch'
import PennyPayment from '../PennyPayment/PennyPayment'
import PennyPaymentConfirm from '../PennyPaymentConfirm/PennyPaymentConfirm'
import PopProcessing from '../PopProcessing/PopProcessing'
import PopUpload from '../PopUpload/PopUpload'
import PostProcessing from '../PostProcessing/PostProcessing'
import PreContract from '../PreContract/PreContract'
import PrintContract from '../PrintContract/PrintContract'
import PrintPreContract from '../PrintPreContract/PrintPreContract'
import Recapitulation from '../Recapitulation/Recapitulation'
import storage from '../../utils/localStorage'

import './styles.scss'

type Props = IntlInterface & ConnectedProps<typeof connector>

// Attributes from state.app are not valid/ready until state.app.appReady is true!

export const App = ({
  appReady,

  pathname,
  saveStayDurations,
  showModalDialog,

  translate,
}: Props) => {
  const [isLeavingConfirmation, setIsLeavingConfirmation] = useState(false)
  const firstLoad = useRef<boolean>(true)
  const inactivityHandle = useRef<number | undefined>()
  const middleContainer = useRef<HTMLDivElement | undefined>()

  const stopCounter = useCallback(() => {
    if (inactivityHandle.current) {
      clearTimeout(inactivityHandle.current)
    }
  }, [])

  const restartCounter = useCallback(() => {
    stopCounter()
    if (storage.getItem('enableInactivityTimer')) {
      inactivityHandle.current = window.setInterval(() => {
        storage.setItem('enableInactivityTimer', false)
        showModalDialog(SHOW_INACTIVITY)
        stopCounter()
      }, INACTIVITY_TIMEOUT)
    }
  }, [showModalDialog, stopCounter])

  useEffect(() => {
    const stayDurations = [
      { code: '0_5', text: translate(messages.ClientAdditionalInfo.stay.below5) },
      { code: '5_10', text: translate(messages.ClientAdditionalInfo.stay.from5To10) },
      { code: '10_20', text: translate(messages.ClientAdditionalInfo.stay.from10To20) },
      { code: '20_30', text: translate(messages.ClientAdditionalInfo.stay.from20To30) },
      { code: '30_', text: translate(messages.ClientAdditionalInfo.stay.above30) },
    ]

    saveStayDurations(stayDurations)
  }, [saveStayDurations, translate])

  useEffect(() => {
    storage.setItem('enableInactivityTimer', true)
    window.addEventListener('click', restartCounter)
    window.addEventListener('wheel', restartCounter)
    window.addEventListener('keydown', restartCounter)
    return () => {
      window.removeEventListener('click', restartCounter)
      window.removeEventListener('wheel', restartCounter)
      window.removeEventListener('keydown', restartCounter)
    }
  }, [restartCounter])

  useEffect(() => {
    // Mobile optimization - scroll to middle container
    if (pathname && appReady) {
      if (firstLoad.current) {
        firstLoad.current = false
        window.scrollTo(0, 0)
      } else {
        middleContainer.current && middleContainer.current.scrollIntoView()
      }
    }
  }, [appReady, pathname])

  const cancelLeaving = useCallback(() => {
    setIsLeavingConfirmation(false)
  }, [])

  const leavePage = useCallback(() => {
    getOpenApiInstance()
      .dataLock({})
      .then(() => {
        storage.clear()
        reloadPage()
      })
  }, [])

  const registerMiddleContainerElement = (container: HTMLDivElement) => {
    middleContainer.current = container
  }

  return (
    <Fragment>
      {isLeavingConfirmation && <LeaveConfirmation cancel={cancelLeaving} goBack={leavePage} />}

      <BasicLayout registerMiddleContainerElement={registerMiddleContainerElement}>
        {appReady && (
          <Switch>
            <Route exact path={Routes.AVAILABILITY} component={Availability} />
            <Route exact path={Routes.CLIENT_ADDITIONAL_INFO} component={ClientAdditionalInfo} />
            <Route exact path={Routes.CLIENT_ADDRESS} component={ClientAddress} />
            <Route exact path={Routes.CLIENT_CHECKS} component={ClientChecks} />
            <Route exact path={Routes.CLIENT_DETAILS} component={ClientDetails} />
            <Route exact path={Routes.CLIENT_INFO} component={ClientInfo} />
            <Route exact path={Routes.CLIENT_SCORING} component={ClientScoring} />
            <Route exact path={Routes.CONTRACT_SIGN} component={ContractSign} />
            <Route exact path={Routes.CONTRACT} component={Contract} />
            <Route exact path={Routes.CREATE_CIF} component={CreateCif} />
            <Route exact path={Routes.EXIT} component={ExitScreen} />
            <Route exact path={Routes.FINAL} component={Final} />
            <Route exact path={Routes.FINANCE_EMPLOYER} component={FinanceEmployer} />
            <Route exact path={Routes.FINANCE_EXPENSES} component={FinanceExpenses} />
            <Route exact path={Routes.FINANCE_INCOME} component={FinanceIncome} />
            <Route exact path={Routes.FINANCE_PROFESSION} component={FinanceProfession} />
            <Route exact path={Routes.IDENTITY} component={Identity} />
            <Route exact path={Routes.KO_ONLINE} component={KoOnline} />
            <Route exact path={Routes.KO_SCORING} component={KoScoring} />
            <Route exact path={Routes.LOAN_CONFIGURATOR_RE} component={LoanConfiguratorRe} />
            <Route exact path={Routes.LOAN_CONFIGURATOR} component={LoanConfigurator} />
            <Route exact path={Routes.NOT_IMPLEMENTED_PAGE} component={NotImplementedPage} />
            <Route exact path={Routes.OK_BRANCH} component={OkBranch} />
            <Route exact path={Routes.PENNY_PAYMENT_CONFIRM} component={PennyPaymentConfirm} />
            <Route exact path={Routes.PENNY_PAYMENT} component={PennyPayment} />
            <Route exact path={Routes.POP_PROCESSING} component={PopProcessing} />
            <Route exact path={Routes.POP_UPLOAD} component={PopUpload} />
            <Route exact path={Routes.POST_PROCESSING} component={PostProcessing} />
            <Route exact path={Routes.PRE_CONTRACT} component={PreContract} />
            <Route exact path={Routes.PRINT_CONTRACT} component={PrintContract} />
            <Route exact path={Routes.PRINT_PRE_CONTRACT} component={PrintPreContract} />
            <Route exact path={Routes.RECAPITULATION} component={Recapitulation} />

            <Redirect to={Routes.CLIENT_INFO} />
            <Route component={NotFound} />
          </Switch>
        )}
      </BasicLayout>

      <ModalDialog />
      <ModalLoader />
    </Fragment>
  )
}

const connector = connect(
  (state: ReduxState) => ({
    appReady: appSelectors.selectAppReady(state),
    isLocalhost: appSelectors.selectIsLocalhost(state),
    pathname: state.router.location.pathname,
    wizardSteps: state.wizard.steps,
  }),
  {
    push,
    saveStayDurations: codetableActions.saveStayDurations,
    showModalDialog: modalDialogActions.showModalDialog,
  }
)

export default compose(errorHandler('containers/App/App.tsx'), connector, intl)(App)
